import * as THREE from 'three';
import { EnvironmentSystemBase } from '../EnvironmentSystemBase';
import { LightningConfig } from '../types';

type LightningBolt = {
    group: THREE.Group;
    opacity: number;
    fadePerSec: number;
};

const DEFAULTS: LightningConfig = {
    randomMinMs: 3000,
    randomMaxMs: 8000,
    boltSegments: 30,
    branchCount: 5,
    zigzagStrength: 6,
    fadePerSec: 2.5,
    flashIntensity: 3.0,
    flashHoldMs: 400
};

export class LightningSystem extends EnvironmentSystemBase {
    public config: LightningConfig;
    private bolts: LightningBolt[] = [];
    private lastStrikeMs: number = 0;
    private randomEnabled: boolean = false;

    constructor(name: string, cfg?: Partial<LightningConfig>) {
        super(name);
        this.config = { ...DEFAULTS, ...(cfg || {}) };
    }

    setConfig(cfg: Partial<LightningConfig>): void {
        this.config = { ...this.config, ...cfg };
    }

    toggleRandom(value?: boolean): void {
        this.randomEnabled = value ?? !this.randomEnabled;
    }

    trigger(): void {
        const start = new THREE.Vector3((Math.random()-0.5)*40,  this.config.flashHoldMs * 0.01 + 30, (Math.random()-0.5)*40);
        const end = new THREE.Vector3(start.x + (Math.random()-0.5)*10, 0, start.z + (Math.random()-0.5)*10);
        const bolt = this.createBolt(start, end);
        this.group.add(bolt.group);
        this.bolts.push(bolt);
        this.lastStrikeMs = performance.now();
    }

    protected build(): void {
        // nothing persistent required
    }

    protected onUpdate(dt: number): void {
        // Random strikes
        if (this.randomEnabled) {
            const now = performance.now();
            const nextInterval = this.config.randomMinMs + Math.random() * (this.config.randomMaxMs - this.config.randomMinMs);
            if (now - this.lastStrikeMs > nextInterval) {
                this.trigger();
            }
        }

        // Update bolts
        for (let i = this.bolts.length - 1; i >= 0; i--) {
            const b = this.bolts[i];
            b.opacity -= b.fadePerSec * dt;
            b.opacity = Math.max(0, b.opacity);
            b.group.children.forEach((child: any) => {
                if (child.material) child.material.opacity = b.opacity;
            });
            if (b.opacity <= 0) {
                this.group.remove(b.group);
                this.bolts.splice(i, 1);
            }
        }
    }

    private createBolt(start: THREE.Vector3, end: THREE.Vector3): LightningBolt {
        const lightningGroup = new THREE.Group();
        const segments = this.config.boltSegments;
        const points: THREE.Vector3[] = [];
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const x = THREE.MathUtils.lerp(start.x, end.x, t) + (Math.random()-0.5) * this.config.zigzagStrength * (1 - t * 0.3);
            const y = THREE.MathUtils.lerp(start.y, end.y, t) + (Math.random()-0.5) * this.config.zigzagStrength * 0.5;
            const z = THREE.MathUtils.lerp(start.z, end.z, t) + (Math.random()-0.5) * this.config.zigzagStrength * (1 - t * 0.3);
            points.push(new THREE.Vector3(x,y,z));
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 5, transparent: true, opacity: 1 });
        const bolt = new THREE.Line(geometry, material);
        lightningGroup.add(bolt);

        for (let i = 0; i < this.config.branchCount; i++) {
            const idx = Math.floor(Math.random() * points.length);
            const branchStart = points[idx];
            const branchEnd = branchStart.clone().add(new THREE.Vector3((Math.random()-0.5)*12, -Math.random()*10, (Math.random()-0.5)*12));
            const branchPts: THREE.Vector3[] = [];
            const branchSegments = 12;
            for (let j = 0; j <= branchSegments; j++) {
                const t = j / branchSegments;
                const bx = THREE.MathUtils.lerp(branchStart.x, branchEnd.x, t) + (Math.random()-0.5)*3;
                const by = THREE.MathUtils.lerp(branchStart.y, branchEnd.y, t) + (Math.random()-0.5)*2;
                const bz = THREE.MathUtils.lerp(branchStart.z, branchEnd.z, t) + (Math.random()-0.5)*3;
                branchPts.push(new THREE.Vector3(bx,by,bz));
            }
            const bgeo = new THREE.BufferGeometry().setFromPoints(branchPts);
            const bmat = new THREE.LineBasicMaterial({ color: 0xaaaaff, linewidth: 2, transparent: true, opacity: 0.8 });
            const branch = new THREE.Line(bgeo, bmat);
            lightningGroup.add(branch);
        }

        return { group: lightningGroup, opacity: 1, fadePerSec: this.config.fadePerSec };
    }
}
